import { Button, Heading, VStack, Text, Box, Link } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useContext, useState } from 'react';
import { EmployerFormFields, EmployerStatementRequest } from 'src/@types';
import {
  EmployerStatementEmployeeSection,
  EmployerStatementEmployerSection,
  EmployerStatementEmploymentSection,
  EmployerStatementFinalizeSection,
  EmployerStatementIncomeSection,
  EmployerStatementLoansSection
} from './sections';
import { API } from '../../../../services';
import { getDefaultValues, prepareFormData } from './utils/employer-statement-utils';
import { AppAlertDialog } from '../../../shared/general';
import { scrollTo } from '../../../../utils';
import { FormContext } from '../../../../context/form-context';
import { IntlProvider } from 'react-intl';

export const EmployerStatementForm = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty }
  } = useForm<EmployerFormFields>({
    mode: 'onBlur',
    defaultValues: getDefaultValues()
  });

  const { isLoading, hasError, setLoading, setError } = useContext(FormContext);

  const values = watch();
  const [pdfUrl, setPdfUrl] = useState('#');
  const [activeStep, setActiveStep] = useState<1 | 2>(1);

  async function onSubmit(values: EmployerFormFields) {
    setLoading(true);
    try {
      const res = await API.post<{ url: string }, EmployerStatementRequest>(
        'employerstatement/werkgeversverklaring',
        prepareFormData(values)
      );
      setPdfUrl(res.url);
      setActiveStep(2);
    } catch (error) {
      setLoading(true);
    } finally {
      setLoading(false);
      scrollTo('.react-tools');
    }
  }

  const handleCloseAlert = () => {
    setError(false);
  };

  const Success = () => (
    <Box display={activeStep === 2 ? undefined : 'none'}>
      <Text mb={4}>
        <Link href={pdfUrl} isExternal>
          Download
        </Link>{' '}
        nu de de werkgeversverklaring in pdf formaat. Of{' '}
        <Link onClick={() => setActiveStep(1)}>vul deze opnieuw in.</Link>
      </Text>
      <Text>
        De werkgeversverklaring moet ondertekend zijn (met blauwe pen). Deze verklaring laat je
        ondertekenen door dezelfde persoon die ook de werkgeversverklaring ondertekent. Voor het
        hypotheekadvies heeft de hypotheekadviseur ook informatie nodig over de aanvullende
        voorzieningen die je als werkgever biedt. Heb je deze informatie nog niet verstrekt? Doe dit
        dan gemakkelijk via het{' '}
        <Link
          href={'https://www.hypotheker.nl/online-formulier-aanvullende-voorzieningen/'}
          isExternal>
          {' '}
          online formulier
        </Link>
        .
      </Text>
    </Box>
  );

  return (
    <>
      <IntlProvider locale={'nl'} defaultLocale={'nl'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack display={activeStep === 1 ? undefined : 'none'} spacing={4} align="flex-start">
            <Heading as={'h1'} variant={`h1`}>
              Online werkgeversverklaring
            </Heading>
            <Box pb={2}>
              <Text>
                Geachte werkgever, <br></br> Je werknemer is bezig met de aanvraag voor een
                hypotheek en verzoekt je om bijgaande werkgeversverklaring in te vullen. Banken zijn
                streng in het controleren van deze verklaringen en eisen een nieuwe als iets niet
                goed of niet volledig is ingevuld. Om dat extra werk te voorkomen is het belangrijk
                dat je de verklaring volgens{' '}
                <Link
                  href={'https://www.hypotheker.nl/werkgeversverklaring-richtlijnen/'}
                  isExternal>
                  {' '}
                  de richtlijnen{' '}
                </Link>
                invult.
              </Text>
            </Box>
            <AppAlertDialog title={'let op!'} onClose={handleCloseAlert} open={hasError}>
              <Box>
                Door een technische storing kunnen we nu helaas geen werkgeversverklaring genereren.
                Probeer het opnieuw of{' '}
                <Link
                  href={
                    'https://www.hypotheker.nl/media/4440/formulier-aanvullende-voorzieningen-werkgever2021.pdf'
                  }
                  isExternal={true}>
                  download de invulbare PDF.
                </Link>
              </Box>
            </AppAlertDialog>

            <Box py={8}>
              <EmployerStatementEmployerSection
                heading={'Gegevens werkgever'}
                register={register}
                errors={errors}></EmployerStatementEmployerSection>
            </Box>
            <Box py={8}>
              <EmployerStatementEmployeeSection
                heading={'Gegevens werknemer'}
                register={register}
                errors={errors}></EmployerStatementEmployeeSection>
            </Box>
            <Box py={8}>
              <EmployerStatementEmploymentSection
                heading={'Aard van het dienstverband'}
                register={register}
                values={values}
                errors={errors}></EmployerStatementEmploymentSection>
            </Box>
            <Box py={8}>
              <EmployerStatementIncomeSection
                heading={'Inkomen'}
                register={register}
                errors={errors}></EmployerStatementIncomeSection>
            </Box>
            <Box py={8}>
              <EmployerStatementLoansSection
                heading={'Leningen/Loonbeslag'}
                register={register}
                values={values}
                errors={errors}></EmployerStatementLoansSection>
            </Box>
            <Box py={8}>
              <EmployerStatementFinalizeSection
                heading={'Afronden'}
                register={register}
                errors={errors}></EmployerStatementFinalizeSection>
            </Box>
            <Button
              isLoading={isLoading}
              loadingText={'Bezig met versturen'}
              type="submit"
              colorScheme="orange"
              isDisabled={false}>
              {!isValid && isDirty ? `Wat moet ik nog invullen?` : `Naar stap 2`}
            </Button>
          </VStack>
          <Success />
        </form>
      </IntlProvider>
    </>
  );
};
